import ErrorHandler from '../utils/ErrorHandler';
import { supabase } from './client';

/**
 * Thin wrapper over the `form-resources` edge function (plan §6.2).
 *
 * All resource storage/RPC access is isolated here so a contract change is a
 * one-file fix. Actions:
 *   upload   { formID, filename, mimeType, base64 }   -> { resource }
 *   describe { formID, paths }                        -> { resources }
 *   url      { formID|publicId, path, disposition }   -> { url, expiresAt }
 *
 * Signed URLs are always generated on demand — never persisted.
 */

const invoke = async (body) => {
    // Authenticated `formID` calls carry the session JWT in the body — the edge
    // function authenticates `body.accessToken`, not the transport header.
    // `publicId` calls stay anonymous (public fill).
    let requestBody = body;
    if (body?.formID) {
        const { data: sessionData } = await supabase.auth.getSession();
        const accessToken = sessionData?.session?.access_token;
        // A publicId fallback resolves anonymously — no session required. A
        // formID-only call without a session would 403 server-side anyway.
        if (!accessToken && !body.publicId) {
            const err = new Error('Sign in to work with form documents.');
            err.status = 403;
            throw err;
        }
        if (accessToken) requestBody = { ...body, accessToken };
    }

    const { data, error } = await supabase.functions.invoke('form-resources', { body: requestBody });

    if (error) {
        let detail = error.message;
        try {
            if (error.context && typeof error.context.json === 'function') {
                const errorBody = await error.context.json();
                detail = errorBody?.error || detail;
            }
        } catch { /* response body not JSON — keep default */ }

        const status = error.context?.status;
        const err = new Error(detail || 'Something went wrong with this document.');
        err.status = status;
        if (typeof status === 'number' && status >= 400 && status < 500) {
            // Expected user-facing failures (forbidden, not found, validation)
            // are correctable — don't report to Sentry.
            throw err;
        }
        ErrorHandler(Error(detail), { mutation: 'formResources', action: body?.action, error }, 'Something went wrong with that document');
        throw err;
    }

    return data;
};

/**
 * Upload a document to a form's dedicated resource prefix. Editor only.
 * Returns the staged descriptor `{ path, filename, mimeType, size, source:'upload' }`;
 * the path is persisted by the normal form Save via `resourcePaths`.
 */
const uploadFormResource = async ({ formID, filename, mimeType, base64 }) => {
    const data = await invoke({ action: 'upload', formID, filename, mimeType, base64 });
    return data?.resource || null;
};

/**
 * Describe resource paths — for an editor staging preview or for rendering an
 * authorized saved list. Never an arbitrary storage metadata lookup.
 * Returns descriptors in the same order as `paths` (missing entries may be
 * omitted server-side). `publicId` identifies the form for anonymous fills.
 */
const describeFormResources = async ({ formID, publicId, paths }) => {
    if ((!formID && !publicId) || !Array.isArray(paths) || paths.length === 0) return [];
    const data = await invoke({ action: 'describe', formID, publicId, paths });
    return data?.resources || [];
};

/**
 * Generate a fresh signed URL for a saved form resource. `disposition` is
 * 'inline' (view) or 'attachment' (download); default TTL 300s server-side.
 * Identify the form by `formID` (authenticated) or `publicId` (public published).
 */
const getFormResourceUrl = async ({ formID, publicId, path, disposition = 'inline' }) => {
    if (!path || (!formID && !publicId)) throw new Error('This document is unavailable.');
    const data = await invoke({ action: 'url', formID, publicId, path, disposition });
    if (!data?.url) throw new Error('Unable to open this document. It may have expired — try again.');
    return data;
};

export { uploadFormResource, describeFormResources, getFormResourceUrl };
